/**
 * Remote audio watchdog — auto-recovers participants whose mic track is
 * "subscribed" by LiveKit but whose RTP packets aren't actually flowing.
 *
 * The bug user reported: enter a room of N people, hear nothing from
 * one or more participants. Their video plays fine — we can SEE them
 * speaking — but their audio level indicator stays dead and there's no
 * sound. Browser refresh fixes it. Multiple participants on the same
 * call hit the same symptom independently.
 *
 * Cause: a known class of LiveKit / WebRTC race conditions where a
 * subscriber's transport completes the SDP handshake for the audio
 * m-section but the RTP socket never actually starts delivering. The
 * publisher is sending packets, the SFU is forwarding, but they don't
 * arrive at this subscriber. Refresh works because it forces a fresh
 * offer/answer cycle for every track. We replicate that — for the
 * specific stalled track only — by toggling `setSubscribed`.
 *
 * Detection: poll `RTCRtpReceiver.getStats()` for the inbound-rtp
 * report and watch `bytesReceived`. If it doesn't advance for
 * `STALL_THRESHOLD_MS` and the publisher isn't muted, the track is
 * stalled. Re-subscribing forces a renegotiation.
 *
 * Cooldown: after a recovery attempt we wait `RECOVERY_COOLDOWN_MS`
 * before considering the same track again, so we don't churn through
 * subscribe/unsubscribe cycles if the underlying network is wedged.
 */
import { Track, RoomEvent, type RemoteTrackPublication, type Room } from 'livekit-client'

const STALL_THRESHOLD_MS = 8_000
const CHECK_INTERVAL_MS = 3_000
const RECOVERY_COOLDOWN_MS = 15_000

interface WatchEntry {
  pub: RemoteTrackPublication
  lastBytesReceived: number
  silenceSinceMs: number
  recovering: boolean
}

export interface WatchdogOptions {
  /** Override for tests — defaults to setInterval / clearInterval. */
  setInterval?: typeof setInterval
  clearInterval?: typeof clearInterval
  /** Override Date.now — eases time-travel testing. */
  now?: () => number
  /** Hook called whenever the watchdog re-subscribes a track (tests). */
  onRecover?: (sid: string) => void
}

/**
 * Install the watchdog on a connected room. Returns a teardown closure
 * that the caller invokes on disconnect / unmount so we don't leave a
 * stray interval running into the next session.
 */
export function installRemoteAudioWatchdog(
  room: Room,
  opts: WatchdogOptions = {},
): () => void {
  const watches = new Map<string, WatchEntry>()
  const lastRecoveryAt = new Map<string, number>()
  const setIv = opts.setInterval ?? setInterval
  const clearIv = opts.clearInterval ?? clearInterval
  const now = opts.now ?? (() => Date.now())

  const tryWatch = (pub: RemoteTrackPublication) => {
    if (pub.kind !== Track.Kind.Audio) return
    if (pub.source !== Track.Source.Microphone) return
    if (watches.has(pub.trackSid)) return
    watches.set(pub.trackSid, {
      pub,
      lastBytesReceived: 0,
      silenceSinceMs: now(),
      recovering: false,
    })
  }

  const drop = (sid: string) => {
    watches.delete(sid)
    lastRecoveryAt.delete(sid)
  }

  // Bootstrap with whatever's already published.
  room.remoteParticipants.forEach((p) => {
    p.trackPublications.forEach((pub) => tryWatch(pub as RemoteTrackPublication))
  })

  const onPublished = (pub: any) => tryWatch(pub as RemoteTrackPublication)
  const onUnpublished = (pub: any) => drop((pub as RemoteTrackPublication).trackSid)
  const onSubscribed = (_track: any, pub: any) => tryWatch(pub as RemoteTrackPublication)
  const onUnsubscribed = (_track: any, pub: any) => drop((pub as RemoteTrackPublication).trackSid)
  room.on(RoomEvent.TrackPublished, onPublished)
  room.on(RoomEvent.TrackUnpublished, onUnpublished)
  room.on(RoomEvent.TrackSubscribed, onSubscribed)
  room.on(RoomEvent.TrackUnsubscribed, onUnsubscribed)

  const tick = async () => {
    for (const [sid, entry] of watches) {
      if (entry.recovering) continue

      // Server-side mute is the legitimate reason `bytesReceived` won't
      // advance — don't try to "recover" a muted speaker into being heard.
      if (entry.pub.isMuted) {
        entry.silenceSinceMs = now()
        continue
      }

      const track: any = entry.pub.track
      const receiver: RTCRtpReceiver | undefined = track?.receiver
      if (!receiver || typeof receiver.getStats !== 'function') continue

      let stats: RTCStatsReport | null = null
      try {
        stats = await receiver.getStats()
      } catch {
        continue
      }
      if (!stats) continue

      let bytes = 0
      stats.forEach((report: any) => {
        if (report.type === 'inbound-rtp') {
          bytes = Math.max(bytes, Number(report.bytesReceived ?? 0))
        }
      })

      if (bytes > entry.lastBytesReceived) {
        entry.lastBytesReceived = bytes
        entry.silenceSinceMs = now()
        continue
      }

      // No bytes since last check — has it been long enough to recover?
      if (now() - entry.silenceSinceMs < STALL_THRESHOLD_MS) continue
      const lastFix = lastRecoveryAt.get(sid)
      // Cooldown applies only AFTER a recovery has happened. Without
      // this guard `lastRecoveryAt.get() ?? 0` would block the very
      // first recovery whenever `now - 0 < COOLDOWN`.
      if (lastFix !== undefined && now() - lastFix < RECOVERY_COOLDOWN_MS) continue

      entry.recovering = true
      lastRecoveryAt.set(sid, now())
      console.warn(
        `[remoteAudioWatchdog] re-subscribing stalled audio track sid=${sid}`,
        ` (no bytes for ${Math.round((now() - entry.silenceSinceMs) / 1000)}s)`,
      )
      try {
        entry.pub.setSubscribed(false)
        // Brief pause so LiveKit fully tears down before we ask again.
        // Without this the second setSubscribed sometimes coalesces with
        // the first and the renegotiation never fires.
        await new Promise((r) => setTimeout(r, 250))
        entry.pub.setSubscribed(true)
        opts.onRecover?.(sid)
      } catch (e) {
        console.warn('[remoteAudioWatchdog] re-subscribe failed:', e)
      } finally {
        entry.recovering = false
        // Reset baseline so we measure flow from THIS recovery onwards.
        entry.lastBytesReceived = 0
        entry.silenceSinceMs = now()
      }
    }
  }

  const handle = setIv(() => { void tick() }, CHECK_INTERVAL_MS)

  return () => {
    clearIv(handle)
    room.off(RoomEvent.TrackPublished, onPublished)
    room.off(RoomEvent.TrackUnpublished, onUnpublished)
    room.off(RoomEvent.TrackSubscribed, onSubscribed)
    room.off(RoomEvent.TrackUnsubscribed, onUnsubscribed)
    watches.clear()
    lastRecoveryAt.clear()
  }
}

// Exported for tests so we don't have to mirror the magic numbers.
export const _watchdogInternals = {
  STALL_THRESHOLD_MS,
  CHECK_INTERVAL_MS,
  RECOVERY_COOLDOWN_MS,
}
